import { Box, Button, Stack, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTranslation } from 'react-i18next';
import { InlineStatus } from 'components/InlineStatus';
import { EvacZone, RegionEvacZoneStyle } from 'shared/types';
import { GeoEventRegion } from 'features/geoEvents/geoEventRegion.types';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import { Evacuations } from '../Evacuations';

export type EvacuationsFormSectionProps = {
  isExpanded: boolean;
  onToggle: () => void;
  isSubmitting: boolean;
  evacZones: EvacZone[];
  zoneStyles: RegionEvacZoneStyle[];
  isLoading: boolean;
  isError: boolean;
  /** The incident's counties. Defaults to the form's `regions`. */
  incidentRegions?: GeoEventRegion[];
  disabled?: boolean;
};

export const EvacuationFormSection = (
  props: EvacuationsFormSectionProps,
): JSX.Element => {
  const {
    isExpanded,
    onToggle,
    isSubmitting,
    evacZones,
    zoneStyles,
    isLoading,
    isError,
    incidentRegions,
    disabled = false,
  } = props;
  const { t } = useTranslation();
  const unstructuredEvacuationsEnabled = useReleaseFlag(
    'unstructured_evacuations',
  );

  return (
    <Stack>
      <Button
        variant="text"
        color="inherit"
        startIcon={
          isExpanded ? <KeyboardArrowDownIcon /> : <ChevronRightIcon />
        }
        onClick={onToggle}
        disabled={isSubmitting}
        sx={{ alignSelf: 'flex-start' }}
      >
        <Typography variant="h3">
          {t('createEditWildfire.evacuations')}
        </Typography>
      </Button>

      {/* Ideally we would use MUI's <Collapse /> here, but there
          are some performance problems with iOS Safari:
          https://linear.app/watchduty/issue/ENG-1377/slowdown-on-certain-add-report-pages */}
      <Box
        sx={{
          display: isExpanded ? 'block' : 'none',
        }}
      >
        {isLoading || isError ? (
          <InlineStatus status={isLoading ? 'loading' : 'error'} />
        ) : (
          <Evacuations
            evacZones={evacZones}
            zoneStyles={zoneStyles}
            // Do not render evacuation table if its hidden with CSS to avoid layout errors from MUI
            shouldRenderTable={isExpanded}
            showUnstructuredEvacuations={unstructuredEvacuationsEnabled}
            incidentRegions={incidentRegions}
            disabled={disabled}
          />
        )}
      </Box>
    </Stack>
  );
};
